import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { actions, selectors } from '@lbmx/app-state';
import { Marketplace } from '@lbmx/types';
import { TranslocoService } from '@ngneat/transloco';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  finalize,
  map,
  tap,
} from 'rxjs/operators';
import { ConfigProvider } from 'src/app/provider/config-provider';
import { CampaignPopupService } from '../../services/campaignPopup/campaignPopup.service';
import { SecureService } from './secure.service';

@Component({
  selector: 'app-marketplace-modal',
  template: `<p-dialog
    [(visible)]="showModal"
    [showHeader]="false"
    [modal]="true"
    position="center"
    [draggable]="false"
    [resizable]="false"
    [responsive]="true"
    [closeOnEscape]="false"
    styleClass="marketplace-selector-modal"
    data-testid="marketplace-selector-modal"
  >
    <lib-working-spinner-component
        [visible]="loading"
    ></lib-working-spinner-component>
    <div class=" vertical-flow marketplace-selector">
      <h2 class="center">{{ 'COMMON.MY_MARKETPLACES' | transloco }}</h2>
      <div class="center space-before-000">
        {{ 'COMMON.MARKETPLACE_SELECTION_INFO' | transloco }}
      </div>
      <div
        *ngFor="let marketplace of marketplaces; let i = index"
        [id]="'marketplace_' + (i + 1)"
        class="vertical-flow space-between-150 raised clickable"
        [ngClass]="i == 0 ? 'space-before-025 ' : 'space-before-100'"
        (click)="setModalMarketplace(marketplace.accountKey.toString())"
      >
        <div class="horizontal-flow">
          <ng-container *ngIf="marketplace.logo; else logoMissing">
            <div
              class="img-container marketplace-logo"
              [id]="'marketplace_logo_' + (i + 1)"
            >
              <img [src]="marketplace.logo" class="target" /></div
          ></ng-container>
          <ng-template #logoMissing>
            <div
              class="img-container marketplace-logo no-img"
              [id]="'marketplace_no_logo_' + (i + 1)"
            >
              <span>
                {{ 'COMPANY.LOGO_DEFAULT_MSG_1' | transloco }}<br />{{
                  'COMPANY.LOGO_DEFAULT_MSG_2' | transloco
                }}
              </span>
            </div>
          </ng-template>
          <div
            class="vertical-flow space-between-000 overflow-wrap"
            [id]="'marketplace_details_' + (i + 1)"
          >
            <div *ngIf="marketplace.legalName" class="name">
              {{ marketplace.legalName }}
            </div>
            <div *ngIf="marketplace.dbaName">{{ marketplace.dbaName }}</div>
            <div *ngIf="marketplace.address1">{{ marketplace.address1 }}</div>
            <div *ngIf="marketplace.address2">{{ marketplace.address2 }}</div>
            <div *ngIf="marketplace.city || marketplace.provinceName">
              {{ marketplace.city }}
              <span *ngIf="marketplace.provinceName">{{
                marketplace.provinceName
              }}</span>
            </div>
            <div *ngIf="marketplace.postalCode">
              {{ marketplace.postalCode }}
            </div>
            <div *ngIf="marketplace.countryName">
              {{ marketplace.countryName }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </p-dialog>`,
})
export class MarketplaceModalComponent implements OnInit, OnDestroy {
  public marketplaces: string | Marketplace[] = [];
  public showModal: boolean;
  public loading = false;
  public subscriptions: Subscription[] = [];
  private lastCall: number = Date.now();
  private onLoad = false;

  @Input('showMarketplaceModal') set showMarketplaceModalSetter(time: number) {
    if (time || 0 > this.lastCall) {
      this.setMarketplace('');
    }
    this.lastCall = time;
  }

  constructor(
    private configPrv: ConfigProvider,
    private router: Router,
    private store: Store,
    private translocoService: TranslocoService,
    private secureService: SecureService,
    private campaignPopupService: CampaignPopupService
  ) {}

  public ngOnInit(): void {

    const errorEmptyMarketplaces = this.store
      .pipe(
        select(selectors.marketplaces),
        filter(({marketplaces }) => marketplaces?.length === 0)
      )
      .subscribe(() =>
          this.redirectToErrorPage()
      );

    const promptSelectMarketplace = this.store
      .pipe(
        select(selectors.marketplaces),
        filter(({marketplace, marketplaces}) => marketplace === '' && marketplaces?.length > 1 && marketplaces instanceof Array)
      )
      .subscribe(({marketplaces}) => {
        this.secureService.setSelectedMarketplace(false);
        this.showModal = true;
        this.marketplaces = marketplaces;
      });

    const useDefaultMarketplace = this.store
      .pipe(
        select(selectors.marketplaces),
        filter(({marketplace, marketplaces}) => marketplace === '' && marketplaces?.length === 1 && marketplaces instanceof Array)
      )
      .subscribe(({marketplaces: [marketplace]}) => {
        this.setModalMarketplace(`${(marketplace as Marketplace).accountKey}`);
      });

    const hideWhenMarketplaceSelected = this.store
      .pipe(
        select(selectors.marketplaces),
        filter(({marketplace, marketplaces}) =>
          marketplace !== '' &&
          marketplaces instanceof Array &&
          marketplaces?.map((m) => m.accountKey.toString()).includes(marketplace))
      )
      .subscribe(() => {
        this.secureService.setSelectedMarketplace(true);
        this.showModal = false;
        this.loading = false;
      });

    this.subscriptions.push(
      errorEmptyMarketplaces,
      promptSelectMarketplace,
      useDefaultMarketplace,
      hideWhenMarketplaceSelected,
      this.store
        .pipe(
          select(selectors.marketplaces),
          map(({ marketplace }) => marketplace),
          distinctUntilChanged(),
          filter((marketplace) => marketplace !== ''),
          tap(() => {
            this.store.dispatch(
              actions.loadAnalyticsConfig({
                endPoints: this.configPrv.AppSetting.uriAnalytics,
              })
            );
          }),
          filter(() => this.onLoad),
          tap(() => {
            this.campaignPopupService.trigger$.next();
          })
        )
        .subscribe()
    );
  }

  public ngOnDestroy() {
    this.secureService.setSelectedMarketplace(false);
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private redirectToErrorPage() {
    this.subscriptions.push(
      this.translocoService
        .selectTranslate('COMMON')
        .pipe(
          tap((translate) => {
            this.router.navigate(['/error'], {
              state: {
                title: translate.MARKETPLACE_ERROR,
                message: translate.MARKETPLACE_CONFIG_INFO,
              },
            });
          })
        )
        .subscribe()
    );
  }

  public setMarketplace(marketplace: string) {
    this.store.dispatch(
      actions.updateUserProfileMarketplace({
        marketplace,
        endPoints: this.configPrv.AppSetting,
      })
    );
  }

  public setModalMarketplace(marketplace: string) {
    this.loading = true;
    this.onLoad = true;
    this.setMarketplace(marketplace);
  }
}
